import { formatNumberToRu } from 'utils';

import type { CellFocusedEvent, CellPosition, GetRowIdParams, GridApi, TabToNextCellParams } from 'ag-grid-community';
import type { SuppressKeyboardEventParams } from 'ag-grid-community/dist/types/core/entities/colDef';

export const getRowId = (params: GetRowIdParams): string => String(params.data.id);

export const numberFormatter =
    (decimal: number) =>
    ({ value }: { value: number }) =>
        formatNumberToRu(value, decimal);

export const minMaxDecimalNumberFormatter =
    (minDecimal: number, maxDecimal: number) =>
    ({ value }: { value: number }) =>
        formatNumberToRu(value, minDecimal, maxDecimal);

const findNextEnableIndex = (table: Element, colId: string, initialIndex: number, backwards: boolean, api: GridApi) => {
    if (!colId) {
        return initialIndex;
    }
    const escapedColId = CSS.escape(colId);

    let resultIndex = initialIndex;
    let hasEnabledInput = false;

    while (!hasEnabledInput) {
        resultIndex = backwards ? resultIndex - 1 : resultIndex + 1;

        const rowNodes = table?.querySelectorAll(`[row-index='${resultIndex}']`);

        if (!rowNodes?.length) {
            resultIndex = initialIndex;
            break;
        }

        // ibs merge confilct
        api.ensureIndexVisible(resultIndex);

        hasEnabledInput = !!table?.querySelector(
            `[row-index='${resultIndex}'] [col-id='${escapedColId}'] input:not([disabled])`,
        );
    }

    return resultIndex;
};

export const tabToNextCellByColumn = (params: TabToNextCellParams): CellPosition | null => {
    const { previousCellPosition, api, backwards, context } = params;
    const { column, rowPinned, rowIndex } = previousCellPosition;

    const tableUniqueId = (context as { tableUniqueId?: string } | undefined)?.tableUniqueId;

    if (!tableUniqueId) {
        return null;
    }

    const table = document.getElementsByClassName(tableUniqueId)[0] as Element | undefined;

    if (!table) {
        return null;
    }

    const displayedRowCount = api.getDisplayedRowCount();

    const nextRowIndex = findNextEnableIndex(table, column.getColId(), rowIndex, backwards, api);

    let clamped = nextRowIndex;

    if (clamped < 0) {
        clamped = 0;
    }

    if (clamped >= displayedRowCount) {
        clamped = displayedRowCount - 1;
    }

    return {
        rowIndex: clamped,
        column,
        rowPinned,
    };
};

export const focusInputOnCellFocused = (event: CellFocusedEvent) => {
    const target = document.activeElement;

    if (!target || !event?.forceBrowserFocus) {
        return;
    }

    if (target.tagName === 'INPUT') {
        (target as HTMLInputElement).focus();
        (target as HTMLInputElement).setSelectionRange(0, 0);
    } else {
        const input = (target as HTMLDivElement).getElementsByTagName('input')?.[0];

        input?.focus();
        input?.setSelectionRange(0, 0);
    }
};

export const suppressKeyboardEventForInput = (params: SuppressKeyboardEventParams) =>
    ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(params.event.key);
export const updateRows = (items: { itemId?: any; clientId?: any; id?: any }[], api?: GridApi) => {
    if (items?.length && api) {
        items.forEach((item) => {
            const rowNode = api.getRowNode(String(item?.clientId ?? item.itemId ?? item.id));

            rowNode?.setData(item);
        });
    }
};
